import { now as nowFunc, startInterval } from '@ewn/time';
import { computed, getCurrentInstance, onUnmounted, ref } from 'vue-demi';

const tickIntervalMilliseconds = 100;

// For performance reasons, we don't want an interval for every caller
// of useClock so we'll use a global ref to track the current time instead
const now = ref(null);

// We also don't need to tick when nobody is watching so we'll
// keep track of how many are watching to determine when to stop
let stopTickingFn = null;
let watcherCount = 0;

function tick () {
  now.value = nowFunc();
}

function tryStart () {
  watcherCount++;
  if (stopTickingFn) return;
  tick(); // Initialize
  stopTickingFn = startInterval(tick, tickIntervalMilliseconds);
}

function tryStop () {
  if (!stopTickingFn) return;
  watcherCount--;
  if (watcherCount) return;
  stopTickingFn();
  stopTickingFn = null;
}

/**
 * @typedef {Object} ClockOptions
 * @property {Boolean} immediate - Indicates if the clock should start immediately (default: `true`).
 */

/**
 * @typedef {Object} ClockApi
 * @property {ComputedRef<Date>} now - A reactive reference to the current date.
 * @property {ComputedRef<Number>} nowUnix - A reactive reference to the current
 * number of milliseconds elapsed since the Unix epoch.
 * @property {Function} pause - Pause the clock.
 * @property {Function} resume - Resume the clock.
 */

/**
 * A reactive wrapper for the system clock with controls.
 *
 * Alternative to useTimestamp from VueUse with background support (not requestAnimationFrame).
 * @param {ClockOptions} options - The options for configuring the clock.
 *
 * @return {ClockApi} The clock.
 */
export function useClock (options = {}) {
  const {
    immediate = true
  } = options;

  // We can't always use `now` since it's global
  const pausedOn = ref(immediate ? null : nowFunc());
  const nowOrLastPaused = computed(() => pausedOn.value || now.value);
  const nowUnix = computed(() => nowOrLastPaused.value.getTime());

  function pause () {
    if (pausedOn.value) return;
    // Use last tick instead of `nowFunc` to avoid triggering a change
    pausedOn.value = now.value;
    tryStop();
  }

  function resume () {
    if (!pausedOn.value) return;
    tryStart();
    pausedOn.value = null;
  }

  if (immediate) {
    tryStart();
  }

  if (getCurrentInstance()) {
    onUnmounted(pause);
  }

  return {
    now: nowOrLastPaused,
    nowUnix,
    pause,
    resume
  };
}
